import {
  STATION_LICENSE_STATE_LABELS,
  STATION_LICENSE_STATES,
  STATION_LICENSE_TYPE_LABELS,
  STATION_LICENSE_TYPES,
  type StationLicense,
  stationLicenseStateSchema,
  stationLicenseTypeSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { BadgeCheck, Download, Pencil, Plus, Power, PowerOff } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LicenseDialog } from "../../../features/market/license-dialog.js";
import { licenseDays } from "../../../features/market/license-values.js";
import {
  licensesQuery,
  type LicenseFilters,
  useLicenses,
} from "../../../features/market/purchasing-queries.js";
import {
  useMarketPermissions,
  useMarketSettings,
  useRefreshMarket,
} from "../../../features/market/queries.js";
import {
  LicenseStateBadge,
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  useMarketStations,
} from "../../../features/market/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { formatDate } from "../../../lib/format.js";
import { usePermissions } from "../../../lib/session.js";

const searchSchema = z.object({
  ...marketSearchShape,
  type: stationLicenseTypeSchema.optional().catch(undefined),
  state: stationLicenseStateSchema.optional().catch(undefined),
  inactive: z.enum(["true"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/market/lisanslar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: LicensesRoute,
});

/**
 * İstasyon satış belgeleri — tütün / alkol / diğer (Prompt 16 — web, aşama B; ADR 0016 §18, mevzuat sorusu #62).
 * v1'de satışı ENGELLEMEZ; durum (geçerli / yakında bitecek / süresi dolmuş) market ayarındaki uyarı gününe göre
 * sunucuda hesaplanır ve bitişe yaklaşınca olay üretilir (uyum takvimi). Okuma `market.read`, yazma
 * `market.license.manage`; belge dosyası depolama akışıyla (`files.upload`, indirme `files.read`).
 */
function LicensesRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketLicenses")}
      description={t("market.licenses.description")}
      breadcrumb={[{ label: t("menu.marketLicenses") }]}
    >
      {({ companyId }) => <LicensesBody companyId={companyId} />}
    </MarketPage>
  );
}

function LicensesBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useMarketPermissions();
  const canDownload = usePermissions().has("files.read");
  const refresh = useRefreshMarket();
  const station = useMarketStations(companyId);
  const settings = useMarketSettings(companyId, station.stationId);
  const [dialog, setDialog] = useState<{ license: StationLicense | null } | null>(null);
  const canManage = permissions.canManageLicenses;

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters: LicenseFilters = {
    stationId: station.stationId ?? undefined,
    licenseType: search.type,
    state: search.state,
    includeInactive: search.inactive === "true" || search.state === "INACTIVE",
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useLicenses(companyId, filters, pages.cursor);

  const toggleActive = useMutation({
    mutationFn: async (license: StationLicense) =>
      (
        await api.PATCH("/market/licenses/{id}", {
          params: { path: { id: license.id } },
          body: { isActive: !license.isActive, version: license.version } as never,
        })
      ).data!,
    onSuccess: async (saved) => {
      await refresh();
      toast({
        tone: "success",
        title: t(saved.isActive ? "market.licenses.activated" : "market.licenses.deactivated", {
          no: saved.documentNo,
        }),
      });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  const download = useMutation({
    mutationFn: async (fileId: string) =>
      (await api.GET("/files/{id}/download-link", { params: { path: { id: fileId } } })).data!,
    onSuccess: (data) => window.open(data.url, "_blank", "noopener"),
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  const columns = useMemo<DataTableColumn<StationLicense>[]>(
    () => [
      {
        id: "document",
        header: t("market.licenses.fields.documentNo"),
        hideable: false,
        exportValue: (row) => row.documentNo,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="font-mono whitespace-nowrap">{row.documentNo}</span>
            <span className="text-xs text-muted-foreground">
              {STATION_LICENSE_TYPE_LABELS[row.licenseType]}
            </span>
          </span>
        ),
      },
      {
        id: "station",
        header: t("market.fields.station"),
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName,
      },
      {
        id: "issuer",
        header: t("market.licenses.fields.issuer"),
        exportValue: (row) => row.issuer,
        cell: (row) => row.issuer ?? "—",
      },
      {
        id: "validity",
        header: t("market.licenses.fields.validity"),
        exportValue: (row) => `${row.validFrom} – ${row.validUntil ?? ""}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="tabular whitespace-nowrap">
              {formatDate(row.validFrom)} –{" "}
              {row.validUntil ? formatDate(row.validUntil) : t("market.licenses.open")}
            </span>
            <DaysText license={row} />
          </span>
        ),
      },
      {
        id: "state",
        header: t("common.status"),
        exportValue: (row) => STATION_LICENSE_STATE_LABELS[row.state],
        cell: (row) => <LicenseStateBadge state={row.state} />,
      },
      {
        id: "file",
        header: t("market.licenses.fields.file"),
        exportValue: (row) => row.fileName,
        cell: (row) =>
          row.fileId === null ? (
            <span className="text-muted-foreground">—</span>
          ) : canDownload ? (
            <Button
              variant="ghost"
              size="sm"
              aria-label={t("market.licenses.downloadLabel", { name: row.fileName ?? row.documentNo })}
              loading={download.isPending && download.variables === row.fileId}
              onClick={() => download.mutate(row.fileId!)}
            >
              <Download aria-hidden="true" />
              <span className="max-w-40 truncate">{row.fileName ?? t("market.licenses.file")}</span>
            </Button>
          ) : (
            <span className="text-sm">{row.fileName}</span>
          ),
      },
      ...(canManage
        ? [
            {
              id: "actions",
              header: t("common.actions"),
              hideable: false,
              cell: (row: StationLicense) => (
                <span className="flex flex-wrap gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t("market.licenses.editLabel", { no: row.documentNo })}
                    onClick={() => setDialog({ license: row })}
                  >
                    <Pencil aria-hidden="true" />
                    {t("common.edit")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t(
                      row.isActive ? "market.licenses.deactivateLabel" : "market.licenses.activateLabel",
                      {
                        no: row.documentNo,
                      },
                    )}
                    loading={toggleActive.isPending && toggleActive.variables?.id === row.id}
                    onClick={() => toggleActive.mutate(row)}
                  >
                    {row.isActive ? <PowerOff aria-hidden="true" /> : <Power aria-hidden="true" />}
                    {row.isActive ? t("market.licenses.deactivate") : t("market.licenses.activate")}
                  </Button>
                </span>
              ),
            } satisfies DataTableColumn<StationLicense>,
          ]
        : []),
    ],
    [t, canManage, canDownload, download, toggleActive],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        {canManage ? (
          <Button className="ms-auto" onClick={() => setDialog({ license: null })}>
            <Plus aria-hidden="true" />
            {t("market.licenses.new")}
          </Button>
        ) : null}
      </Card>
      {settings.data ? (
        <p className="text-sm text-muted-foreground">
          {t("market.licenses.warningHint", { days: settings.data.licenseWarningDays })}
        </p>
      ) : null}

      <DataTable
        caption={t("market.licenses.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<BadgeCheck />}
            title={t("market.licenses.emptyTitle")}
            description={t("market.licenses.emptyDescription")}
          />
        }
        filters={
          <>
            <FormField label={t("market.licenses.fields.type")} className="w-60">
              {(control) => (
                <Select
                  {...control}
                  value={search.type ?? ""}
                  onChange={(event) => setSearch({ type: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {STATION_LICENSE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {STATION_LICENSE_TYPE_LABELS[type]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("common.status")} className="w-52">
              {(control) => (
                <Select
                  {...control}
                  value={search.state ?? ""}
                  onChange={(event) => setSearch({ state: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {STATION_LICENSE_STATES.map((state) => (
                    <option key={state} value={state}>
                      {STATION_LICENSE_STATE_LABELS[state]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <Checkbox
                checked={search.inactive === "true"}
                onChange={(event) => setSearch({ inactive: event.target.checked ? "true" : undefined })}
              />
              {t("market.licenses.includeInactive")}
            </label>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<StationLicense>(
            async (cursor) =>
              (
                await api.GET("/market/licenses", {
                  params: { query: licensesQuery(companyId, filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "satis-belgeleri",
            sheetName: t("menu.marketLicenses"),
            columns: visible,
            rows,
          });
        }}
      />

      {canManage ? (
        <LicenseDialog
          open={dialog !== null}
          onOpenChange={(open) => !open && setDialog(null)}
          stations={station.stations}
          defaultStationId={station.stationId}
          license={dialog?.license ?? null}
        />
      ) : null}
    </>
  );
}

function DaysText({ license }: { license: StationLicense }): ReactElement | null {
  const { t } = useTranslation();
  const days = licenseDays(license);
  if (days === null) return null;
  return (
    <span className="text-xs text-muted-foreground">
      {days.key === "today"
        ? t("market.licenses.expiresToday")
        : days.key === "left"
          ? t("market.licenses.daysLeft", { count: days.count })
          : t("market.licenses.daysAgo", { count: days.count })}
    </span>
  );
}
